import React from 'react';

/**
 * Política de privacidade — página pública em /privacidade.
 *
 * Não exige login: é o endereço que vai no cadastro do app da Meta e no
 * rodapé do login. O texto é estático e vive aqui em JSX (não em Markdown
 * renderizado em tempo de execução) para não puxar biblioteca nova só para
 * uma página. Ao atualizar o texto, atualize também a data em LAST_UPDATE.
 */
const LAST_UPDATE = '15 de setembro de 2026';
const CONTACT_EMAIL = 'admin@limparauto.com.br';

export default function PrivacyPage() {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <a className="legal-brand" href="/">
          <img src="/icons/app-192.png" alt="" />
          <span>New Car CRM</span>
        </a>
        <a className="legal-back" href="/">Ir para o login</a>
      </header>

      <main className="legal-content">
        <article className="legal-card">
          <h1>Política de Privacidade</h1>
          <p className="legal-updated">Última atualização: {LAST_UPDATE}</p>

          <section>
            <h2>1. Quem somos</h2>
            <p>
              O <strong>New Car CRM</strong> é um sistema de gestão de relacionamento com clientes
              voltado a concessionárias e revendas de veículos, desenvolvido e operado pela{' '}
              <strong>LIMPAR SERVIÇOS AUTOMOTIVOS LTDA</strong> (CNPJ 51.871.406/0001-80), com sede na
              Rua Ribeiro de Brito, 1002, sala 906, Recife/PE, CEP 51021-310.
            </p>
            <p>
              Esta política explica quais dados pessoais o sistema trata, com que finalidade, por
              quanto tempo e quais são os direitos das pessoas titulares desses dados, em
              conformidade com a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais —
              LGPD).
            </p>
          </section>

          <section>
            <h2>2. A quem esta política se aplica</h2>
            <p>
              O New Car CRM é contratado por concessionárias, que o utilizam para registrar e
              acompanhar o atendimento de seus clientes. Nessa relação:
            </p>
            <ul>
              <li>
                A <strong>concessionária contratante</strong> é a <strong>controladora</strong> dos
                dados: é ela quem decide quais dados coleta e para quê.
              </li>
              <li>
                A <strong>LimpAr</strong> atua como <strong>operadora</strong>: trata os dados em
                nome da concessionária, seguindo suas instruções e os limites desta política.
              </li>
            </ul>
            <p>
              Se você é cliente de uma concessionária e quer exercer direitos sobre seus dados,
              pode nos procurar pelos canais da seção 9 — encaminharemos sua solicitação à
              concessionária responsável ou a atenderemos diretamente, conforme o caso.
            </p>
          </section>

          <section>
            <h2>3. Quais dados tratamos</h2>
            <p>
              <strong>Dados de clientes das concessionárias</strong>, informados no momento da
              visita à loja:
            </p>
            <ul>
              <li>Nome completo</li>
              <li>CPF</li>
              <li>Telefone/WhatsApp</li>
              <li>E-mail (quando fornecido)</li>
              <li>Veículos de interesse</li>
              <li>Observações do atendimento registradas pela equipe da loja</li>
              <li>Histórico de mensagens trocadas pelos canais de atendimento</li>
            </ul>
            <p>
              <strong>Dados de usuários do sistema</strong> (funcionários da concessionária):
            </p>
            <ul>
              <li>Nome, e-mail e perfil de acesso</li>
              <li>Telefone, quando cadastrado para recebimento de notificações</li>
              <li>Registros de acesso e de ações realizadas no sistema</li>
            </ul>
            <p>
              Não tratamos dados pessoais sensíveis, nem realizamos decisões automatizadas que
              produzam efeitos jurídicos sobre os titulares.
            </p>
          </section>

          <section>
            <h2>4. Para que usamos os dados</h2>
            <ul>
              <li>Registrar a visita do cliente à loja e o seu interesse em veículos</li>
              <li>
                Enviar mensagem de acompanhamento (follow-up) após a visita, por WhatsApp ou
                e-mail
              </li>
              <li>
                Permitir que a equipe da concessionária responda e dê continuidade ao atendimento
              </li>
              <li>Gerar relatórios internos de desempenho comercial para a concessionária</li>
              <li>Operar recursos de atendimento automatizado, conforme a seção 6</li>
            </ul>
            <p>
              As bases legais aplicáveis são a <strong>execução de contrato</strong> e o{' '}
              <strong>legítimo interesse</strong> da concessionária em acompanhar seus
              atendimentos, nos termos do art. 7º da LGPD.
            </p>
          </section>

          <section>
            <h2>5. Comunicações por WhatsApp e e-mail</h2>
            <p>
              O sistema envia mensagens automáticas de acompanhamento após a visita do cliente à
              loja, utilizando a <strong>WhatsApp Business Platform</strong>, da Meta, e serviços
              de envio de e-mail transacional.
            </p>
            <p>
              O conteúdo dessas mensagens limita-se ao contexto do atendimento realizado.{' '}
              <strong>
                Nenhum dado pessoal é utilizado para publicidade de terceiros ou vendido a
                terceiros.
              </strong>
            </p>
            <p>
              O cliente pode solicitar a interrupção das mensagens a qualquer momento,
              respondendo à própria conversa com pedidos como "parar", "sair" ou "não quero
              receber mensagens". O sistema reconhece essas solicitações e interrompe os envios
              automaticamente.
            </p>
          </section>

          <section>
            <h2>6. Atendimento automatizado</h2>
            <p>
              Parte do atendimento inicial pode ser realizada por um assistente virtual baseado
              em inteligência artificial, que responde dúvidas sobre os veículos disponíveis no
              estoque da loja.
            </p>
            <p>
              O assistente <strong>se identifica como automatizado</strong> na primeira resposta
              de cada conversa e transfere o atendimento a um vendedor humano sempre que o
              cliente solicitar, ou quando o assunto envolver preço, prazo, financiamento,
              avaliação de usado ou qualquer condição comercial.
            </p>
            <p>
              Para gerar as respostas, trechos da conversa podem ser processados por provedores
              de modelos de linguagem contratados pela LimpAr. Esses provedores atuam como
              suboperadores e não utilizam o conteúdo para treinar seus modelos.
            </p>
          </section>

          <section>
            <h2>7. Compartilhamento de dados</h2>
            <p>
              Os dados são compartilhados apenas com prestadores de serviço necessários ao
              funcionamento do sistema:
            </p>
            <ul>
              <li>
                <strong>Provedor de infraestrutura em nuvem</strong>, onde o sistema e o banco de
                dados são hospedados
              </li>
              <li>
                <strong>Meta Platforms</strong>, para envio e recebimento de mensagens pelo
                WhatsApp
              </li>
              <li>
                <strong>Provedor de envio de e-mail transacional</strong>
              </li>
              <li>
                <strong>Provedor de modelo de linguagem</strong>, para o atendimento automatizado
              </li>
            </ul>
            <p>
              Não vendemos, alugamos ou cedemos dados pessoais a terceiros para fins
              publicitários ou comerciais.
            </p>
          </section>

          <section>
            <h2>8. Segurança e retenção</h2>
            <p>Adotamos medidas técnicas e organizacionais para proteger os dados, incluindo:</p>
            <ul>
              <li>Transmissão criptografada (HTTPS) em todas as comunicações</li>
              <li>Criptografia das credenciais de integração armazenadas</li>
              <li>Controle de acesso por perfil de usuário</li>
              <li>
                Isolamento de dados entre concessionárias, de modo que uma loja nunca acessa
                dados de outra
              </li>
            </ul>
            <p>
              Os dados são mantidos enquanto durar a relação contratual com a concessionária e
              pelo prazo necessário ao cumprimento de obrigações legais. Encerrado o contrato, os
              dados são excluídos ou devolvidos à concessionária, conforme sua instrução.
            </p>
          </section>

          <section>
            <h2>9. Direitos dos titulares</h2>
            <p>Nos termos da LGPD, você pode solicitar a qualquer momento:</p>
            <ul>
              <li>Confirmação da existência de tratamento dos seus dados</li>
              <li>Acesso aos dados que mantemos</li>
              <li>Correção de dados incompletos, inexatos ou desatualizados</li>
              <li>
                Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em
                desconformidade com a lei
              </li>
              <li>Portabilidade dos dados</li>
              <li>Informação sobre com quem compartilhamos seus dados</li>
              <li>Revogação do consentimento, quando essa for a base legal aplicável</li>
            </ul>
            <p>
              Para exercer esses direitos, entre em contato pelo e-mail{' '}
              <strong>
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </strong>
              . Responderemos no prazo previsto em lei.
            </p>
          </section>

          <section>
            <h2>10. Alterações desta política</h2>
            <p>
              Esta política pode ser atualizada para refletir mudanças no sistema ou na
              legislação. A data da última atualização é sempre indicada no topo do documento.
              Alterações relevantes serão comunicadas às concessionárias contratantes.
            </p>
          </section>

          <section>
            <h2>11. Contato</h2>
            <address className="legal-address">
              <strong>LIMPAR SERVIÇOS AUTOMOTIVOS LTDA</strong>
              <br />
              CNPJ 51.871.406/0001-80
              <br />
              Rua Ribeiro de Brito, 1002, sala 906 — Recife/PE — CEP 51021-310
              <br />
              E-mail: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </address>
          </section>
        </article>
      </main>

      <footer className="legal-footer">
        New Car CRM · LimpAr Serviços Automotivos · <a href="/">Voltar ao login</a>
      </footer>
    </div>
  );
}
